import Parse from 'parse';
import { returnUrlQueryParams } from 'src/app/auth/utils/return-url.util';

export const ORIGINAL_SESSION_TOKEN_KEY = 'originalSessionToken';
export const IMPERSONATED_SESSION_TOKEN_KEY = 'impersonatedSessionToken';
export const LOGIN_PATH = '/auth/login';

const PUBLIC_ROOT_SEGMENTS = ['auth', 'download'];

interface ParseRequestOptions {
  sessionToken?: string;
}

interface ParseRESTController {
  request(
    method: string,
    path: string,
    data: unknown,
    options?: ParseRequestOptions,
  ): Promise<unknown>;
  ajax(...args: unknown[]): Promise<unknown>;
}

interface ParseCloudController {
  run(
    name: string,
    data: Record<string, unknown>,
    options: ParseRequestOptions,
  ): Promise<unknown>;
}

interface ParseCoreManager {
  getRESTController(): ParseRESTController;
  setRESTController(controller: ParseRESTController): void;
  getCloudController(): ParseCloudController;
}

interface ParseUserWithMe {
  me(sessionToken: string): Promise<Parse.User>;
}

let expiredSessionHandled = false;

export function clearImpersonationSessionTokens(): void {
  localStorage.removeItem(ORIGINAL_SESSION_TOKEN_KEY);
  localStorage.removeItem(IMPERSONATED_SESSION_TOKEN_KEY);
}

export function isExpiredSessionError(error: unknown): boolean {
  return (
    error instanceof Parse.Error &&
    error.code === Parse.Error.INVALID_SESSION_TOKEN
  );
}

export function runCloudFunctionWithoutSession(
  name: string,
  data: Record<string, unknown>,
): Promise<unknown> {
  return parseCoreManager()
    .getCloudController()
    .run(name, data, { sessionToken: '' });
}

export function verifySessionToken(sessionToken: string): Promise<Parse.User> {
  return (Parse.User as unknown as ParseUserWithMe).me(sessionToken);
}

export function installExpiredSessionHandler(): void {
  const coreManager = parseCoreManager();
  const controller = coreManager.getRESTController();

  coreManager.setRESTController({
    ...controller,
    request: (method, path, data, options) => {
      const sentToken = options?.sessionToken ?? currentSessionToken();
      return controller
        .request(method, path, data, options)
        .catch((error: unknown) => {
          if (
            isExpiredSessionError(error) &&
            sentToken &&
            sentToken === currentSessionToken()
          ) {
            void handleExpiredSession(sentToken);
          }
          throw error;
        });
    },
  });
}

function parseCoreManager(): ParseCoreManager {
  return Parse.CoreManager as unknown as ParseCoreManager;
}

function currentSessionToken(): string | undefined {
  try {
    return Parse.User.current()?.getSessionToken();
  } catch {
    return undefined;
  }
}

function storedSessionToken(): string | undefined {
  try {
    const stored = localStorage.getItem(
      `Parse/${Parse.applicationId}/currentUser`,
    );
    const user: unknown = stored ? JSON.parse(stored) : null;
    if (
      typeof user === 'object' &&
      user !== null &&
      'sessionToken' in user &&
      typeof user.sessionToken === 'string'
    ) {
      return user.sessionToken;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

function isPublicPath(pathname: string): boolean {
  return PUBLIC_ROOT_SEGMENTS.includes(pathname.split('/')[1]);
}

function loginUrlReturningTo(returnUrl: string): string {
  const query = new URLSearchParams(returnUrlQueryParams(returnUrl)).toString();
  return query ? `${LOGIN_PATH}?${query}` : LOGIN_PATH;
}

async function handleExpiredSession(failedToken: string): Promise<void> {
  if (expiredSessionHandled) {
    return;
  }
  expiredSessionHandled = true;

  const storedToken = storedSessionToken();
  if (storedToken && storedToken !== failedToken) {
    window.location.reload();
    return;
  }

  clearImpersonationSessionTokens();
  await Parse.User.logOut().catch(() => undefined);

  if (currentSessionToken()) {
    expiredSessionHandled = false;
    return;
  }

  if (isPublicPath(window.location.pathname)) {
    window.location.reload();
  } else {
    window.location.href = loginUrlReturningTo(
      window.location.pathname + window.location.search,
    );
  }
}
